import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { ErrorMessage } from 'formik';
import { ChoiceOption } from 'types/common';
import { Firm, Office } from 'types/firm';
import { FeeScheduleOption, FirmOptions, FormAction, OfficeFormValues } from 'types/firmInfo';

import { Spacer, makeStyles } from '@bequestinc/wui';
import { FormHelperText, Grid, TextField, Typography } from '@mui/material';

import { useGlobalContext } from 'hooks';
import { useGetFeeSchedulesQuery } from 'hooks/query-hooks/fee-schedules/useFeeScheduleQuery';
import { FirmsQueryKey, useFirmOptionsQuery } from 'hooks/query-hooks/firms/useFirmsQuery';
import {
  useCreateOfficeMutation,
  useUpdateOfficeMutation,
} from 'hooks/query-hooks/offices/useOfficeMutation';
import { useCurrentUserQuery } from 'hooks/query-hooks/users/useUserQuery';

import {
  ACTIVE,
  ADD,
  CAN_EDIT_FEE_SCHEDULE,
  CAN_EDIT_OFFICE_ATTORNEYS_AND_SERVICES,
  CONFIRM,
  LANGUAGES,
  ON_HOLD,
  REGISTER,
  UPDATE,
} from 'utils/constants';
import { PREVIOUSLY_DELETED_OFFICE_MESSAGE } from 'utils/constants/firmInfo';
import parseErrors from 'utils/errors';
import formatServiceForSubmit from 'utils/office';
import formatPhoneNumber from 'utils/validators';

import AddressFields from 'components/AddressFields';
import ChipSelector from 'components/ChipSelector';
import PhoneNumberField from 'components/custom_fields/PhoneNumberField';
import Checkbox from 'components/form/Checkbox';
import Form from 'components/form/Form';
import FormAreaWithAlert from 'components/form/FormAreaWithAlert';
import SelectField from 'components/form/SelectField';
import AttorneyServicesSelector from 'components/pages/firm-info/AttorneyServicesSelector';
import OfficeFormButtons from 'components/pages/firm-info/OfficeFormButtons';
import ReactivateOfficeModal from 'components/pages/firm-info/ReactivateOfficeModal';
import { officeSchema } from 'components/pages/firm-info/schema';

const useStyles = makeStyles()({
  textField: {
    width: '100%',
  },
  shortTextField: {
    width: '95%',
  },
  card: {
    padding: '20px',
  },
  underReviewText: {
    marginTop: '10px',
    marginLeft: '20px',
    marginBottom: '10px',
  },
});

interface PropTypes {
  office: Partial<Office>;
  handleSubmit?: ((values: OfficeFormValues) => void) | null;
  canUpdate: boolean;
  action: FormAction;
  afterSubmitAction?: () => void;
}

const OfficeInfoForm = ({
  office,
  handleSubmit = null,
  canUpdate,
  action,
  afterSubmitAction = () => {},
}: PropTypes) => {
  const { classes } = useStyles();
  const { services } = useGlobalContext();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState('');
  const [restrictedServiceTypeIds, setRestrictedServiceTypeIds] = useState<number[]>(
    office.restrictedServiceTypeIds,
  );
  const [showReactivateOfficeModal, setShowReactivateOfficeModal] = useState(false);

  const { data: user } = useCurrentUserQuery();
  const { data: firmOptions = { culturalFluency: [], specialServices: [] } } =
    useFirmOptionsQuery() as { data?: FirmOptions };
  const { data: feeSchedules = [] } = useGetFeeSchedulesQuery({ inNetwork: true }) as {
    data?: FeeScheduleOption[];
  };

  const addToCache = (data: Office) =>
    queryClient.setQueryData<Firm>([...FirmsQueryKey, office.firm], oldData => ({
      ...oldData,
      offices: oldData.offices.concat(data),
    }));

  const updateCache = (data: Office) =>
    queryClient.setQueryData<Firm>([...FirmsQueryKey, office.firm], oldData => ({
      ...oldData,
      offices: oldData.offices.map(oldOffice => (oldOffice.id === data.id ? data : oldOffice)),
    }));

  const canPanelEditAttorneysAndServices = user.isPanelUser
    ? user.groups.includes(CAN_EDIT_OFFICE_ATTORNEYS_AND_SERVICES)
    : true;

  const parseOfficeError = (response: AxiosResponse) => {
    const isBadRequestStatusCode = response && response.status === 400;
    const isAddressError =
      response && typeof response.data === 'object' && 'latitude' in response.data;
    if (isBadRequestStatusCode && isAddressError) {
      return 'Registering office failed! Check your address information and try again.';
    }
    return parseErrors(response);
  };

  const createOfficeMutation = useCreateOfficeMutation({
    onSuccess: (data: Office | string) => {
      if (typeof data === 'string' && data.includes(PREVIOUSLY_DELETED_OFFICE_MESSAGE)) {
        setShowReactivateOfficeModal(true);
        return;
      }
      addToCache(data as Office);
      afterSubmitAction();
    },
    onError: (error: AxiosError) => {
      setErrorMessage(parseOfficeError(error.response));
    },
  });

  const updateOfficeMutation = useUpdateOfficeMutation({
    onSuccess: (data: Office) => {
      updateCache(data);
      afterSubmitAction();
    },
    onError: (error: AxiosError) => setErrorMessage(parseOfficeError(error.response)),
  });

  const handleFormSubmit = async (formValues: OfficeFormValues) => {
    const formattedValues = formatServiceForSubmit(services, formValues);
    formattedValues.attorneyInfos =
      formValues.attorneyInfos.length > 0
        ? office.firmAttorneys
            .filter(atty => (formValues.attorneyInfos as string[]).includes(atty.name))
            .map(atty => atty.pk)
        : [];
    if (handleSubmit) {
      handleSubmit(formattedValues as OfficeFormValues);
    } else if (action === ADD) {
      const response = await createOfficeMutation.mutateAsync({ ...formattedValues });
      // Prevent the success popup when a previously deleted office needs reactivation.
      if (typeof response === 'string' && response.includes(PREVIOUSLY_DELETED_OFFICE_MESSAGE)) {
        throw new Error('Office reactivation required');
      }
    } else {
      await updateOfficeMutation.mutateAsync({ ...formattedValues });
    }
  };

  return (
    <Form
      initialValues={{
        action,
        status: office.status || (action === REGISTER ? ON_HOLD : ACTIVE),
        id: office.id || null,
        firm: office.firm || null,
        country: office.country || '',
        street: office.street || '',
        street2: office.street2 || '',
        city: office.city || '',
        state: office.state || '',
        zipCode: office.zipCode || '',
        phone: formatPhoneNumber(office.phone) || '',
        phoneExtension: office.phoneExtension || '',
        email: office.email || '',
        languages: office.languages || ([ADD, REGISTER].includes(action) ? ['English'] : []),
        numberOfAttorneys:
          action === ADD || action === REGISTER ? 1 : office.numberOfAttorneys || '',
        serviceTypes: office.serviceTypes || [],
        specialServices: firmOptions?.specialServices.filter(service =>
          office.specialServices?.includes(service[0]),
        ),
        restrictedServiceTypeIds: services.serviceOptions.filter(service =>
          office.restrictedServiceTypeIds?.includes(service[0]),
        ),
        fieldsUnderReview: office.fieldsUnderReview,
        engagementScore: [ADD, REGISTER].includes(action) ? 5 : office.engagementScore,
        addressInfoConfirmed: false,
        serviceInfoConfirmed: false,
        forConfirmationModal: action === CONFIRM,
        feeSchedule: office.feeSchedule,
        attorneyInfos: office.attorneyInfos?.map(atty => atty.name) || [],
      }}
      validationSchema={officeSchema}
      handleFormSubmit={handleFormSubmit}
      shouldReset={false}
      className=""
      validateOnChange
      validateOnBlur
      validateOnMount
      enableReinitialize
      doNotRenderButtons
      initialTouched={{
        addressInfoConfirmed: true,
        serviceInfoConfirmed: true,
        attorneyInfos: true,
        languages: true,
        email: true,
        phone: true,
        serviceTypes: true,
      }}
      showErrors={false}
    >
      <Grid container direction="column" spacing={2} className={classes.card}>
        {user.isPanelRepUser && (
          <>
            <Grid item>
              <SelectField
                name="status"
                label="Status"
                disabled={!canUpdate || action === REGISTER}
                className={classes.textField}
                menuItems={[ACTIVE, ON_HOLD]}
              />
            </Grid>
            <Grid item>
              <TextField
                name="engagementScore"
                label="Engagement Score"
                className={classes.textField}
                disabled={!canUpdate || !user.groups.includes(CAN_EDIT_FEE_SCHEDULE)}
                helperText={
                  canUpdate &&
                  !user.groups.includes(CAN_EDIT_FEE_SCHEDULE) &&
                  'You do not have the proper permissions to edit this field'
                }
              />
            </Grid>
            <Grid item>
              <TextField
                name="numberOfAttorneys"
                label="Number of Attorneys"
                className={classes.textField}
                disabled={!canUpdate}
              />
            </Grid>
          </>
        )}

        <FormAreaWithAlert active={Boolean(action === CONFIRM)}>
          {office.fieldsUnderReview &&
            Object.keys(office.fieldsUnderReview).length > 0 &&
            canUpdate && (
              <Typography color="text.disabled" className={classes.underReviewText}>
                Cannot edit address while changes are under review by MLP
                {'address' in office.fieldsUnderReview && (
                  <>
                    <br />
                    Address under review:{' '}
                    {office.fieldsUnderReview['address']
                      .replace(/\*\*/g, ', ')
                      .replace(/\*/g, ', ')}
                  </>
                )}
              </Typography>
            )}
          <AddressFields
            disabled={
              !canUpdate ||
              (office.fieldsUnderReview && Object.keys(office.fieldsUnderReview).length > 0)
            }
            fieldsUnderReview={office.fieldsUnderReview}
            maxLengths={{ street: 64, street2: 64, city: 64 }}
          />
          <Spacer v={10} />
          {action === CONFIRM && (
            <Checkbox
              type="checkbox"
              name="addressInfoConfirmed"
              label="This information is correct"
            />
          )}
        </FormAreaWithAlert>

        <Grid container item direction="row" spacing={2}>
          <Grid item xs={6}>
            <PhoneNumberField
              name="phone"
              label="Phone"
              className={classes.textField}
              disabled={!canUpdate}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="phoneExtension"
              label="Phone Extension"
              inputProps={{
                maxLength: 6,
              }}
              className={classes.textField}
              disabled={!canUpdate}
            />
          </Grid>
        </Grid>

        <Grid item>
          <ChipSelector
            name="languages"
            options={LANGUAGES}
            label="Office Staff Languages"
            textFieldStyle={classes.textField}
            disabled={!canUpdate}
            type="autocomplete"
          />
        </Grid>

        <Grid item>
          <TextField
            name="email"
            label="Email"
            disabled={!canUpdate}
            className={classes.textField}
          />
        </Grid>

        {action !== REGISTER && (
          <>
            <Grid item>
              <Typography>Attorneys</Typography>
            </Grid>
            <Grid item>
              <ChipSelector
                name="attorneyInfos"
                options={office.firmAttorneys.map(atty => atty.name)}
                label="Attorneys"
                textFieldStyle={classes.textField}
                disabled={!canUpdate}
                type="autocomplete"
              />
            </Grid>
          </>
        )}

        <FormAreaWithAlert active={Boolean(action === CONFIRM)}>
          <Typography>Practice Areas:</Typography>
          <Spacer v={12} />
          <AttorneyServicesSelector
            name="serviceTypes"
            disabled={!canUpdate || !canPanelEditAttorneysAndServices}
            restrictedServices={restrictedServiceTypeIds}
          />
          <Spacer v={10} />
          {action === CONFIRM && (
            <Checkbox
              type="checkbox"
              name="serviceInfoConfirmed"
              label="This information is correct"
            />
          )}
        </FormAreaWithAlert>

        <ErrorMessage
          name="serviceTypes"
          render={msg => (
            <FormHelperText error style={{ fontSize: '16px', margin: '16px 16px 24px' }}>
              {msg}
            </FormHelperText>
          )}
        />

        <Grid item>
          <ChipSelector
            name="specialServices"
            options={firmOptions.specialServices}
            label="Special Services"
            textFieldStyle={classes.textField}
            disabled={!canUpdate || !canPanelEditAttorneysAndServices}
            type="autocomplete"
          />
        </Grid>

        {user.isPanelUser && (
          <>
            <Grid item>
              <ChipSelector
                name="restrictedServiceTypeIds"
                options={services.serviceOptions as ChoiceOption[]}
                label="Restricted Services"
                onChange={(_event, _setFieldValue, value: [number, string][]) =>
                  setRestrictedServiceTypeIds(value.map(option => option[0]))
                }
                textFieldStyle={classes.textField}
                disabled={!canUpdate || !canPanelEditAttorneysAndServices}
                type="autocomplete"
              />
            </Grid>
            <Grid item>
              <SelectField
                name="feeSchedule"
                label="Fee Schedule"
                disabled={!canUpdate}
                menuItems={feeSchedules.map(feeSchedule => ({
                  value: feeSchedule.pk,
                  label: `${feeSchedule.description} (${feeSchedule.locationCode})`,
                }))}
                className={classes.textField}
              />
            </Grid>
          </>
        )}

        <Spacer v={24} />
        <OfficeFormButtons enabled={canUpdate} action={action} office={office} />

        {(action === ADD || action === UPDATE) && (
          <Typography fontWeight="bold" color="error" style={{ whiteSpace: 'pre-line' }}>
            {errorMessage}
          </Typography>
        )}
      </Grid>
      {showReactivateOfficeModal && (
        <ReactivateOfficeModal
          open={showReactivateOfficeModal}
          onClose={() => setShowReactivateOfficeModal(false)}
          addToCache={addToCache}
          afterSubmitAction={afterSubmitAction}
          firmAttorneys={office.firmAttorneys}
        />
      )}
    </Form>
  );
};

export default observer(OfficeInfoForm);
